import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of, ReplaySubject } from 'rxjs';
import { SharedService } from '../shared/shared.service';
import { FormService } from '../shared/form.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, mergeMap, concatMap, tap } from 'rxjs/operators';
import { first, shareReplay } from 'rxjs/operators';
import { OfferService } from '../shared/offer.service';
import * as moment from 'moment';
import { LocalStorageService } from 'ngx-localstorage';
import { KpiEnum } from '../interfaces/kpi.enum';
import { SessionStorageService } from 'ngx-webstorage';
import { LoadOfferService } from '../shared/loadOffer.service';
import { TranslateService } from '@ngx-translate/core';
import { LoadedFromCPService } from '../shared/loaded-from-customer-portal.service';

@Injectable({
  providedIn: 'root',
})
export class ProposalGuard implements CanActivate {
  subject = new ReplaySubject<any>(1);

  constructor(
    public sharedService: SharedService,
    public http: HttpClient,
    private storage: LocalStorageService,
    public router: Router,
    public offerService: OfferService,
    public loadOfferService: LoadOfferService,
    public loadedFromCPService: LoadedFromCPService,
    public formService: FormService,
    private sessionStorage: SessionStorageService,
    private translateService: TranslateService
  ) {}

  loadProductProposals(route, currentFormData?) {
    let formData =
      currentFormData || this.formService?.currentForm?.getRawValue();
    if (!formData) {
      return of(this.router.createUrlTree(['/start']));
    }
    const {
      propertyCategory,
      propertySubcategory,
      postCode,
      currency,
      city,
      purpose,
      yearOfConstruction,
      street,
      area,
      claimsExists,
      immovableinsuranceSum,
      immovableinsuranceSumRecommended,
      immovableinsuranceSumRecommendedCurr2,
      movableinsuranceSum,
      movableinsuranceSumRecommended,
      movableinsuranceSumRecommendedCurr2
    } = formData;
    const data = {
      propertyCategory,
      propertySubcategory,
      postCode,
      currency,
      city,
      purpose,
      yearOfConstruction,
      street,
      area,
      claimsExists,
      immovableinsuranceSum,
      immovableinsuranceSumRecommended,
      immovableinsuranceSumRecommendedCurr2,
      movableinsuranceSum,
      movableinsuranceSumRecommended,
      movableinsuranceSumRecommendedCurr2,
    };

    this.sharedService.calculatePremiums$ = null;
    this.sharedService.getProductProposals(data, this.formService.currentForm);
    // this.sharedService.calculatePremiums$ = this.http.post('/api/v1/calculatePremiums/', data)
    this.sharedService.calculatePremiums$
      .pipe(
        catchError((error) => {
          this.sharedService.loadingStep = false;
          return of(this.router.createUrlTree(['somethingWentWrong']));
        }),
        mergeMap((res: any) => {
          if (res) {
            route.data = { ...route.data, proposals: res, currentFormData };
            return of(true);
          } else {
            return of(this.router.createUrlTree(['somethingWentWrong']));
          }
        })
      )
      .subscribe((res) => {
        this.subject.next(res);
        this.sharedService.loadingStep = false;
      });
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    this.sharedService.loadingStep = {
      title: 'loading.homeInfo',
      image: 'woman-loading-page',
    };

    if (route.queryParams.id && route.queryParams.email) {
      const params = new HttpParams({
        fromObject: {
          id: route.queryParams.id,
          email: route.queryParams.email,
        },
      });

      if(route.queryParams.language){
        const lang = route.queryParams.language.toLowerCase();
        this.storage.set('language', lang);
        this.translateService.use(lang);
      }

      if(route.queryParams.cp){
        this.sharedService.loadedFromCP=true;
        this.loadedFromCPService._isCustomerPortal = true;
      }
      else {
        this.sharedService.isOfferFromEmail = false;
        this.sessionStorage.store(
          'isOfferFromEmail',
          this.sharedService.isOfferFromEmail
        );
      }
      this.http
        .get('/api/v1/review/offer/', { params })
        .pipe(
          tap(() => this.sharedService.sendKpiTrackingData(KpiEnum.OFFER_LINK_COUNT).subscribe()),
          tap(() => {
            this.sharedService.isOfferFromEmail = true;
            this.sessionStorage.store('isOfferFromEmail', this.sharedService.isOfferFromEmail)
          } ),
          catchError((error) => {
            // this.sharedService.loadingStep = false;
            return of(this.router.createUrlTree(['somethingWentWrong']));
          }),
          mergeMap((res: any) => {
            // this.sharedService.loadingStep = false;
            if (res) {
              if (!res.length) {
                return of(this.router.createUrlTree(['expired-offer']));
              }

              this.offerService.savedOfferId = route.queryParams.id;
              const { currentFormData } = this.loadOfferService.loadOffer(res);
              this.formService.currentForm.patchValue(currentFormData);
              this.formService.currentForm.updateValueAndValidity();
              this.loadProductProposals(route, currentFormData.value);
              return of(true);
            } else {
              return of(this.router.createUrlTree(['somethingWentWrong']));
            }
          }),

        )
        .subscribe((res) => {
          if (res === true)
            this.sharedService.loadingStep = {
              title: 'proposal.title',
              image: 'woman-loading-page',
            };
          else {
            this.subject.next(res);
            this.sharedService.loadingStep = false;
            
          }
        });
    } else if (route.queryParams.eBorica) {
      const stateData = this.storage.get('data');

      stateData.insurances.mtplEndDate = moment(
        stateData.insurances?.mtplEndDate
      );
      stateData.insurances.cascoEndDate = moment(
        stateData.insurances?.cascoEndDate
      );
      this.formService.getMainForm().patchValue(stateData);

      this.loadProductProposals(route, stateData);
    } else {
      this.subject = new ReplaySubject<any>(1);
      let formData = this.formService?.currentForm?.getRawValue();
      if (!formData) {
        this.sharedService.loadingStep = false;
        this.sharedService.loadingSpinner = false;
        return this.router.createUrlTree(['somethingWentWrong']);
      }
      this.loadProductProposals(route);
    }
    return this.subject.pipe(first());
  }
}
